import React from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';

import {
  CodeExecutionResultDetail,
  CodeExecutionResultSummary,
  getCodeExecutionResult,
  listCodeExecutionResult,
} from 'stores/CodeExecuteStoreAdapter';
import { formatDateTime } from 'utils/DateTimeFormatter';
import { errorDefault } from 'utils/Toastify';

type Props = {
  open: boolean;
  onClose: () => void;
  /** 実行履歴を取得する対象ファイルのID */
  fileId: string | null;
  /** 選択時に呼ばれる（seq / arguments / stdout プレビュー用） */
  onSelect: (item: { seq: number; arguments: string | null; stdout: string | null }) => void;
  title?: string;
};

/** judge0 status.id==3 は Accepted（正常終了） */
const isAccepted = (statusId: number) => statusId === 3;

/**
 * 指定ファイルの実行履歴一覧を表示し、期待値の由来となる実行結果を選択させるダイアログ。
 * 選択した実行の引数（arguments）と標準出力（stdout）が、そのまま期待値の入出力になる。
 * 各行をホバー / 展開すると arguments / stdout をプレビューできる。
 */
const ExecutionHistorySelectDialog: React.FC<Props> = ({
  open,
  onClose,
  fileId,
  onSelect,
  title = '実行履歴を選択',
}) => {
  const [summaries, setSummaries] = React.useState<CodeExecutionResultSummary[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [expanded, setExpanded] = React.useState<number | null>(null);
  const [details, setDetails] = React.useState<Record<number, CodeExecutionResultDetail>>({});

  React.useEffect(() => {
    if (!open || !fileId) return;
    setLoading(true);
    setExpanded(null);
    setDetails({});
    listCodeExecutionResult(fileId)
      .then(setSummaries)
      .catch(() => errorDefault('実行履歴の取得に失敗しました'))
      .finally(() => setLoading(false));
  }, [open, fileId]);

  const loadDetail = React.useCallback(
    async (seq: number) => {
      if (!fileId || details[seq]) return;
      try {
        const d = await getCodeExecutionResult(fileId, seq);
        setDetails((prev) => ({ ...prev, [seq]: d }));
      } catch {
        errorDefault('実行結果の取得に失敗しました');
      }
    },
    [fileId, details],
  );

  const toggleExpand = (seq: number) => {
    if (expanded === seq) {
      setExpanded(null);
    } else {
      setExpanded(seq);
      loadDetail(seq);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        elevation: 0,
        sx: { borderRadius: 3, border: 1, borderColor: 'divider' },
      }}
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary">
          選択した実行の「実行時引数」と「標準出力」が期待値としてコピー保持されます
        </Typography>
        <Box
          sx={{
            mt: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'auto',
            maxHeight: '55vh',
            minHeight: 160,
          }}
        >
          {loading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                読み込み中...
              </Typography>
            </Box>
          ) : summaries.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                このファイルの実行履歴がありません
              </Typography>
            </Box>
          ) : (
            summaries.map((s) => {
              const detail = details[s.seq];
              const isOpen = expanded === s.seq;
              return (
                <Box key={s.seq} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 1,
                      transition: 'background-color 0.15s',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Chip
                      size="small"
                      label={`#${s.seq}`}
                      sx={{ borderRadius: 1, fontWeight: 600 }}
                    />
                    <Chip
                      size="small"
                      icon={isAccepted(s.statusId) ? <CheckCircleIcon /> : undefined}
                      label={s.statusDescription}
                      color={isAccepted(s.statusId) ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                      {formatDateTime(s.createdAt)}
                    </Typography>
                    <Typography
                      variant="caption"
                      onClick={() => toggleExpand(s.seq)}
                      sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 600 }}
                    >
                      {isOpen ? 'プレビューを閉じる' : 'プレビュー'}
                    </Typography>
                    <Typography
                      variant="caption"
                      onClick={() =>
                        onSelect({
                          seq: s.seq,
                          arguments: detail?.arguments ?? null,
                          stdout: detail?.stdout ?? null,
                        })
                      }
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        fontWeight: 700,
                        px: 1,
                      }}
                    >
                      選択
                    </Typography>
                  </Box>
                  {isOpen && (
                    <Box sx={{ px: 2, pb: 1.5, bgcolor: 'action.hover' }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        実行時引数
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          mb: 1,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          border: 1,
                          borderColor: 'divider',
                          fontSize: 12,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                        }}
                      >
                        {detail ? (detail.arguments ?? '(引数なし)') : '読み込み中...'}
                      </Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        標準出力
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          m: 0,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                          border: 1,
                          borderColor: 'divider',
                          fontSize: 12,
                          maxHeight: 180,
                          overflow: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                        }}
                      >
                        {detail ? (detail.stdout ?? '(出力なし)') : '読み込み中...'}
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ExecutionHistorySelectDialog;
